import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker, OverlayView } from '@react-google-maps/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Card, CardContent } from '../components/ui/card';
import {
  Building2, MapPin, Info, Users, ScrollText, Sparkles, Image as ImageIcon,
  Check, ChevronLeft, ChevronRight, Upload, X, Loader2, Navigation,
  GripVertical, AlertCircle, Camera, Star
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getSupportedCurrencies } from '../utils/currency';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const hasValidApiKey = GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY !== 'YOUR_GOOGLE_MAPS_API_KEY';
const libraries = ['places'];
const MIN_REQUIRED_IMAGES = 2;

const propertyTypes = [
  { value: 'room', label: 'Room' },
  { value: 'flat', label: 'Flat' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'house', label: 'House' },
  { value: 'villa', label: 'Villa' },
  { value: 'studio', label: 'Studio' },
  { value: 'shared_room', label: 'Shared Room' },
  { value: 'shop', label: 'Shop' },
  { value: 'office', label: 'Office Space' },
  { value: 'commercial', label: 'Commercial' },
];

const currencies = getSupportedCurrencies().slice(0, 40);

const billingPeriods = [
  { value: 'daily', label: 'Per Day' },
  { value: 'weekly', label: 'Per Week' },
  { value: 'monthly', label: 'Per Month' },
];

const steps = [
  { id: 1, title: 'Property Type', icon: Building2 },
  { id: 2, title: 'Location', icon: MapPin },
  { id: 3, title: 'Basic Details', icon: Info },
  { id: 4, title: 'Occupancy & Rules', icon: Users },
  { id: 5, title: 'Facilities', icon: Sparkles },
  { id: 6, title: 'Media', icon: ImageIcon },
  { id: 7, title: 'Review', icon: Check },
];

const defaultFormData = {
  property_type: '',
  title: '',
  description: '',
  monthly_rent: '',
  deposit: '',
  currency: 'USD',
  billing_period: 'monthly',
  negotiable: false,
  location: {
    country: '', city: '', region: '', area: '',
    street_address: '', building_name: '', landmark: '', postal_code: '',
    latitude: null, longitude: null,
  },
  details: {
    floor_number: '', total_floors: '', room_count: 1,
    bedroom_count: 1, bathroom_count: 1, kitchen_count: 1,
    area_sqft: '', furnishing: 'unfurnished',
  },
  occupancy: {
    max_renters: 1, ideal_tenant: '',
    male_allowed: true, female_allowed: true, family_allowed: true,
    couples_allowed: true, students_allowed: true, professionals_allowed: true,
    anyone_allowed: true,
  },
  rules: {
    smoking_allowed: false, alcohol_allowed: false, pets_allowed: false,
    visitors_allowed: true, quiet_hours: '', gate_open_time: '', gate_close_time: '',
    custom_rules: '',
  },
  facilities: {
    wifi: false, water: true, electricity_backup: false,
    attached_bathroom: false, shared_bathroom: false, kitchen_access: false,
    bike_parking: false, car_parking: false, laundry: false, balcony: false,
    cctv: false, security_guard: false, elevator: false, air_conditioning: false,
    heating: false, nearby_transport: false, nearby_market: false,
    nearby_hospital: false, nearby_school: false,
  },
  media: [],
  move_in_date: '',
};

// ===================== LOCATION STEP =====================
function LocationStep({ formData, setFormData, isLoaded }) {
  const [predictions, setPredictions] = useState([]);
  const [locating, setLocating] = useState(false);
  const [reverseGeocoding, setReverseGeocoding] = useState(false);
  const [showPredictions, setShowPredictions] = useState(false);
  const [locationErrors, setLocationErrors] = useState({});
  const mapRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    if (isLoaded && window.google?.maps?.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  const loc = formData.location;
  const hasCoords = loc.latitude != null && loc.longitude != null;
  const mapCenter = hasCoords
    ? { lat: loc.latitude, lng: loc.longitude }
    : { lat: 27.7172, lng: 85.324 };

  const updateLocation = (field, value) => {
    setFormData(prev => ({
      ...prev,
      location: { ...prev.location, [field]: value },
    }));
    if (locationErrors[field]) {
      setLocationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const updateCoords = (lat, lng) => {
    setFormData(prev => ({
      ...prev,
      location: { ...prev.location, latitude: lat, longitude: lng },
    }));
    setLocationErrors(prev => ({ ...prev, coords: null }));
  };

  // Street address autocomplete
  const handleStreetChange = (value) => {
    updateLocation('street_address', value);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (value.length >= 3 && autocompleteService.current && hasValidApiKey) {
      searchTimeout.current = setTimeout(() => {
        autocompleteService.current.getPlacePredictions(
          { input: value, types: ['address', 'establishment', 'geocode'] },
          (preds, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) {
              setPredictions(preds.slice(0, 5));
              setShowPredictions(true);
            } else {
              setPredictions([]);
            }
          }
        );
      }, 300);
    } else if (value.length >= 3) {
      // Fallback: server suggestions
      searchTimeout.current = setTimeout(async () => {
        try {
          const res = await fetch(`${API_URL}/api/search/suggestions?q=${encodeURIComponent(value)}`);
          if (res.ok) {
            const data = await res.json();
            setPredictions((data.suggestions || []).map(s => ({
              description: s.value,
              structured_formatting: { main_text: s.value, secondary_text: s.type },
              place_id: null,
            })));
            setShowPredictions(true);
          }
        } catch { setPredictions([]); }
      }, 300);
    } else {
      setPredictions([]);
      setShowPredictions(false);
    }
  };

  // Select autocomplete suggestion
  const handleSelectPrediction = (prediction) => {
    setShowPredictions(false);
    setPredictions([]);
    updateLocation('street_address', prediction.description);

    if (!prediction.place_id) return;

    if (!placesService.current && mapRef.current) {
      placesService.current = new window.google.maps.places.PlacesService(mapRef.current);
    }

    if (placesService.current) {
      placesService.current.getDetails(
        { placeId: prediction.place_id, fields: ['geometry', 'address_components', 'formatted_address'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            updateCoords(lat, lng);
            mapRef.current?.panTo({ lat, lng });
            mapRef.current?.setZoom(17);

            // Populate country and city from address components
            const comps = place.address_components || [];
            comps.forEach(comp => {
              const types = comp.types;
              if (types.includes('locality')) updateLocation('city', comp.long_name);
              if (types.includes('country')) updateLocation('country', comp.long_name);
            });
          }
        }
      );
    } else {
      // No Places service - try server geocode
      reverseGeocodeFromServer(null, null, prediction.description);
    }
  };

  // Reverse geocode from lat/lng - tries client-side (Google Maps JS API) first, then backend
  const reverseGeocode = async (lat, lng) => {
    setReverseGeocoding(true);
    let resolved = false;

    // Attempt 1: Client-side Google Geocoder (uses Maps JS API)
    if (isLoaded && window.google?.maps?.Geocoder) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results && results[0]) resolve(results[0]);
            else reject(new Error(status));
          });
        });

        // Parse address components with priority fallback
        const comps = result.address_components || [];
        const parts = {};
        comps.forEach(c => {
          if (c.types.includes('street_number')) parts.street_number = c.long_name;
          if (c.types.includes('route')) parts.route = c.long_name;
          if (c.types.includes('neighborhood')) parts.neighborhood = c.long_name;
          if (c.types.includes('sublocality_level_1') || c.types.includes('sublocality')) parts.area = c.long_name;
          if (c.types.includes('point_of_interest') || c.types.includes('establishment')) parts.landmark = c.long_name;
          if (c.types.includes('locality')) parts.city = c.long_name;
          if (c.types.includes('country')) parts.country = c.long_name;
        });

        // Also capture additional component types useful for Asian/Nepali addresses
        comps.forEach(c => {
          if (c.types.includes('administrative_area_level_3')) parts.area3 = c.long_name;
          if (c.types.includes('administrative_area_level_4')) parts.area4 = c.long_name;
          if (c.types.includes('administrative_area_level_5')) parts.area5 = c.long_name;
          if (c.types.includes('premise')) parts.premise = c.long_name;
          if (c.types.includes('natural_feature') || c.types.includes('tourist_attraction') ||
              c.types.includes('place_of_worship') || c.types.includes('park')) {
            if (!parts.landmark) parts.landmark = c.long_name;
          }
        });

        // Build street address with fallback chain
        let street = '';
        if (parts.street_number && parts.route) street = `${parts.street_number} ${parts.route}`;
        else if (parts.route) street = parts.route;
        else if (parts.premise) street = parts.premise;
        else if (parts.neighborhood) street = parts.neighborhood;
        else if (parts.area) street = parts.area;
        else if (parts.area5) street = parts.area5;
        else if (parts.area4) street = parts.area4;
        else if (parts.area3) street = parts.area3;
        else if (parts.landmark) street = parts.landmark;

        // Always use formatted_address as final fallback for street
        if (!street && result.formatted_address) {
          const fmtParts = result.formatted_address.split(',');
          street = fmtParts[0].trim();
          // If first part is just the city/country, use first two parts
          if (street === parts.city || street === parts.country) {
            street = fmtParts.slice(0, 2).join(',').trim();
          }
        }

        if (street) updateLocation('street_address', street);
        if (parts.city) updateLocation('city', parts.city);
        if (parts.country) updateLocation('country', parts.country);
        if (parts.landmark) updateLocation('landmark', parts.landmark);

        if (street || parts.city || parts.country) {
          toast.success('Location detected and address filled automatically.');
          resolved = true;
        }
      } catch {
        // Client-side geocoding failed, try backend
      }
    }

    // Attempt 2: Backend reverse geocode
    if (!resolved) {
      try {
        const res = await fetch(`${API_URL}/api/reverse-geocode?lat=${lat}&lng=${lng}`);
        if (res.ok) {
          const data = await res.json();
          if (data.found) {
            const street = data.street_address || data.area || '';
            if (street) updateLocation('street_address', street);
            if (data.city) updateLocation('city', data.city);
            if (data.country) updateLocation('country', data.country);
            if (data.landmark) updateLocation('landmark', data.landmark);

            if (street || data.city || data.country) {
              toast.success('Location detected and address filled automatically.');
              resolved = true;
            }
          }
        }
      } catch {
        // Backend also failed
      }
    }

    // Attempt 3: Direct Nominatim (OpenStreetMap) fallback - free, no API key needed
    if (!resolved) {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1&accept-language=en`,
          { headers: { 'User-Agent': 'RoomHora/1.0' } }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.address) {
            const addr = data.address;
            const country = addr.country || '';
            const city = addr.city || addr.town || addr.village || addr.municipality || addr.county || addr.state_district || '';
            let street = addr.road || addr.pedestrian || addr.path || addr.footway || '';
            if (addr.house_number && street) street = `${addr.house_number} ${street}`;
            if (!street) street = addr.neighbourhood || addr.suburb || addr.quarter || addr.hamlet || addr.village_district || '';
            if (!street) street = addr.amenity || addr.building || addr.tourism || '';
            // Final fallback: use display_name first segment
            if (!street && data.display_name) street = data.display_name.split(',')[0].trim();
            const landmark = addr.amenity || addr.tourism || addr.building || addr.neighbourhood || addr.suburb || '';

            if (street) updateLocation('street_address', street);
            if (city) updateLocation('city', city);
            if (country) updateLocation('country', country);
            if (landmark) updateLocation('landmark', landmark);

            if (street || city || country) {
              toast.success('Location detected and address filled automatically.');
              resolved = true;
            }
          }
        }
      } catch {
        // Nominatim also failed
      }
    }

    // All methods failed - show helpful message, do NOT fill coordinates as address
    if (!resolved) {
      toast.info("We detected your location but couldn't retrieve the full address. Please adjust the pin or enter the address manually.");
    }

    setReverseGeocoding(false);
  };

  const reverseGeocodeFromServer = async (lat, lng, query) => {
    try {
      const res = await fetch(`${API_URL}/api/geocode?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.found) {
          updateCoords(data.latitude, data.longitude);
          mapRef.current?.panTo({ lat: data.latitude, lng: data.longitude });
          mapRef.current?.setZoom(14);
        }
      }
    } catch { /* silent */ }
  };

  // Map click handler
  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    updateCoords(lat, lng);
    reverseGeocode(lat, lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Marker drag handler
  const handleMarkerDrag = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    updateCoords(lat, lng);
    reverseGeocode(lat, lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use current location
  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setLocating(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true, timeout: 10000, maximumAge: 0,
        });
      });
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      updateCoords(lat, lng);
      mapRef.current?.panTo({ lat, lng });
      mapRef.current?.setZoom(17);
      // reverseGeocode handles success/fallback toasts
      await reverseGeocode(lat, lng);
    } catch (error) {
      if (error.code === 1) {
        toast.error('Location access denied. Please enter the address manually.');
      } else {
        toast.error('Could not detect location. Try entering the address manually.');
      }
    } finally {
      setLocating(false);
    }
  };

  return (
    <div>
      <h2 className="font-serif text-2xl text-slate-900 mb-1">Where is your property?</h2>
      <p className="text-slate-500 mb-6">Enter the exact address and pin the location on the map</p>

      {/* Use Current Location Button */}
      <Button
        variant="outline"
        onClick={handleUseCurrentLocation}
        disabled={locating}
        className="w-full mb-6 h-12 rounded-xl border-dashed border-2 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300"
        data-testid="use-current-location-btn"
      >
        {locating ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Detecting your location...</>
        ) : (
          <><Navigation className="w-4 h-4 mr-2" /> Use My Current Location</>
        )}
      </Button>

      {/* Street Address with Autocomplete */}
      <div className="mb-4 relative">
        <Label className="text-sm font-medium">
          Street Address <span className="text-red-500">*</span>
        </Label>
        <div className="relative mt-1.5">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={loc.street_address}
            onChange={(e) => handleStreetChange(e.target.value)}
            onFocus={() => predictions.length > 0 && setShowPredictions(true)}
            onBlur={() => setTimeout(() => setShowPredictions(false), 200)}
            placeholder="Start typing the street address, road, or landmark..."
            className={`pl-10 h-11 ${locationErrors.street_address ? 'border-red-400 focus:ring-red-400' : ''}`}
            data-testid="location-street-address"
          />
          {reverseGeocoding && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
          )}
        </div>
        {locationErrors.street_address && (
          <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid="street-address-error">
            <AlertCircle className="w-3 h-3" /> {locationErrors.street_address}
          </p>
        )}

        {/* Autocomplete Dropdown */}
        {showPredictions && predictions.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50">
            {predictions.map((pred, idx) => (
              <button
                key={pred.place_id || idx}
                onMouseDown={() => handleSelectPrediction(pred)}
                className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 text-sm transition-colors"
                data-testid={`address-suggestion-${idx}`}
              >
                <MapPin className="w-4 h-4 text-accent flex-shrink-0" />
                <div className="truncate">
                  <span className="font-medium text-slate-900">
                    {pred.structured_formatting?.main_text || pred.description}
                  </span>
                  {pred.structured_formatting?.secondary_text && (
                    <span className="text-slate-500 ml-1">
                      {pred.structured_formatting.secondary_text}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Required Fields Row */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-sm font-medium">
            Country <span className="text-red-500">*</span>
          </Label>
          <Input
            value={loc.country}
            onChange={(e) => updateLocation('country', e.target.value)}
            placeholder="Nepal"
            className={`mt-1.5 ${locationErrors.country ? 'border-red-400' : ''}`}
            data-testid="location-country"
          />
          {locationErrors.country && (
            <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {locationErrors.country}</p>
          )}
        </div>
        <div>
          <Label className="text-sm font-medium">
            City <span className="text-red-500">*</span>
          </Label>
          <Input
            value={loc.city}
            onChange={(e) => updateLocation('city', e.target.value)}
            placeholder="Kathmandu"
            className={`mt-1.5 ${locationErrors.city ? 'border-red-400' : ''}`}
            data-testid="location-city"
          />
          {locationErrors.city && (
            <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {locationErrors.city}</p>
          )}
        </div>
      </div>

      {/* Landmark (optional) */}
      <div className="mb-2">
        <Label className="text-sm text-slate-500">Landmark (optional)</Label>
        <Input
          value={loc.landmark || ''}
          onChange={(e) => updateLocation('landmark', e.target.value)}
          placeholder="Near Boudhanath Stupa"
          className="mt-1.5"
          data-testid="location-landmark"
        />
      </div>

      {/* Map */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-medium">Pin the exact property location on the map or use your current location.</Label>
          {hasCoords && (
            <span className="text-xs text-emerald-600 font-medium flex items-center gap-1" data-testid="coords-display">
              <Check className="w-3 h-3" />
              {loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}
            </span>
          )}
        </div>

        <div className="h-80 rounded-2xl overflow-hidden border-2 border-slate-200">
          {hasValidApiKey && isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapCenter}
              zoom={hasCoords ? 16 : 6}
              onClick={handleMapClick}
              onLoad={(map) => { mapRef.current = map; }}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true,
              }}
            >
              {hasCoords && (
                <Marker
                  position={{ lat: loc.latitude, lng: loc.longitude }}
                  draggable
                  onDragEnd={handleMarkerDrag}
                  data-testid="property-marker"
                />
              )}
            </GoogleMap>
          ) : (
            <div
              className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center cursor-pointer relative"
              style={{
                backgroundImage: 'linear-gradient(rgba(148,163,184,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.15) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }}
            >
              {hasCoords && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-6 h-6 bg-accent rounded-full border-2 border-white shadow-lg" />
                </div>
              )}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm text-center">
                <MapPin className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">Interactive map requires Google Maps API key</p>
                <p className="text-xs text-slate-400 mt-1">Use "Current Location" or enter address manually</p>
              </div>
            </div>
          )}
        </div>

        <p className="text-sm text-slate-500 mt-2">
          Click on the map, drag the marker, or type an address above to set the location
        </p>

        {locationErrors.coords && (
          <p className="text-red-500 text-sm mt-2 flex items-center gap-1 bg-red-50 p-3 rounded-xl" data-testid="coords-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{locationErrors.coords}</span>
          </p>
        )}
      </div>
    </div>
  );
}

// ===================== MEDIA STEP =====================
function MediaStep({ formData, setFormData }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const fileInputRef = useRef(null);

  const media = formData.media;
  const imageCount = media.filter(m => m.type === 'image').length;
  const isMinMet = imageCount >= MIN_REQUIRED_IMAGES;

  const uploadFile = async (file) => {
    const fileId = `${file.name}-${Date.now()}`;
    setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      toast.error(`Unsupported file: ${file.name}`);
      return null;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error(`${file.name} is too large (max 20MB)`);
      return null;
    }

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev[fileId] || 0;
          if (current >= 90) { clearInterval(progressInterval); return prev; }
          return { ...prev, [fileId]: current + 10 };
        });
      }, 100);

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const headers = {};
      const token = localStorage.getItem('token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers,
        body: formDataUpload,
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }

      const data = await response.json();
      setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));

      return {
        url: data.url.startsWith('http') ? data.url : `${API_URL}${data.url}`,
        name: data.original_name || file.name,
        type: data.type || (isImage ? 'image' : 'video'),
        size: data.size || file.size,
        localPreview: URL.createObjectURL(file),
      };
    } catch (error) {
      toast.error(`Failed to upload ${file.name}: ${error.message}`);
      setUploadProgress(prev => { const n = { ...prev }; delete n[fileId]; return n; });
      return null;
    }
  };

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    setUploading(true);
    const results = [];

    for (const file of fileArray) {
      const result = await uploadFile(file);
      if (result) results.push(result);
    }

    if (results.length > 0) {
      setFormData(prev => ({
        ...prev,
        media: [...prev.media, ...results],
      }));
      toast.success(`${results.length} file${results.length > 1 ? 's' : ''} uploaded`);
    }

    setUploading(false);
    setUploadProgress({});
  };

  const handleFileInput = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeMedia = (index) => {
    setFormData(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index),
    }));
  };

  // Reorder: move item to new position
  const moveMedia = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setFormData(prev => {
      const items = [...prev.media];
      const [moved] = items.splice(fromIdx, 1);
      items.splice(toIdx, 0, moved);
      return { ...prev, media: items };
    });
  };

  const handleDragStart = (idx) => setDragItem(idx);
  const handleDragOverItem = (e, idx) => {
    e.preventDefault();
    if (dragItem !== null && dragItem !== idx) {
      moveMedia(dragItem, idx);
      setDragItem(idx);
    }
  };
  const handleDragEnd = () => setDragItem(null);

  const activeUploads = Object.entries(uploadProgress);

  return (
    <div>
      <h2 className="font-serif text-2xl text-slate-900 mb-1">Add Photos & Videos</h2>
      <p className="text-slate-500 mb-6">
        High-quality photos help your listing get noticed and build trust
      </p>

      {/* Photo Count & Status */}
      <div className={`flex items-center justify-between p-4 rounded-xl mb-6 ${
        isMinMet ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'
      }`} data-testid="media-status-bar">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isMinMet ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
          }`}>
            <Camera className="w-5 h-5" />
          </div>
          <div>
            <p className={`font-medium ${isMinMet ? 'text-emerald-800' : 'text-amber-800'}`}>
              {imageCount}/{MIN_REQUIRED_IMAGES} photos uploaded
            </p>
            <p className={`text-xs ${isMinMet ? 'text-emerald-600' : 'text-amber-600'}`}>
              {isMinMet
                ? 'Minimum met! Add more for better visibility.'
                : `Add at least ${MIN_REQUIRED_IMAGES} photos to continue`}
            </p>
          </div>
        </div>
        {isMinMet && <Check className="w-6 h-6 text-emerald-500" />}
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all mb-6 ${
          dragOver
            ? 'border-accent bg-accent/5 scale-[1.01]'
            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
        }`}
        data-testid="media-drop-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleFileInput}
          className="hidden"
          data-testid="media-upload-input"
        />
        <Upload className={`w-10 h-10 mx-auto mb-3 ${dragOver ? 'text-accent' : 'text-slate-300'}`} />
        <p className="text-slate-700 font-medium mb-1">
          {dragOver ? 'Drop files here' : 'Drag & drop or click to upload'}
        </p>
        <p className="text-sm text-slate-400">JPG, PNG, WebP images or MP4 videos (max 20MB each)</p>
      </div>

      {/* Upload Progress */}
      {activeUploads.length > 0 && (
        <div className="space-y-2 mb-6">
          {activeUploads.map(([id, progress]) => (
            <div key={id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
              <Loader2 className="w-4 h-4 text-accent animate-spin" />
              <div className="flex-1">
                <Progress value={progress} className="h-1.5" />
              </div>
              <span className="text-xs text-slate-500">{progress}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Media Grid with Reorder */}
      {media.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="media-grid">
          {media.map((item, index) => (
            <div
              key={index}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOverItem(e, index)}
              onDragEnd={handleDragEnd}
              className={`relative aspect-square rounded-xl overflow-hidden group border-2 transition-all ${
                dragItem === index ? 'border-accent opacity-50' : 'border-transparent hover:border-slate-300'
              }`}
              data-testid={`media-item-${index}`}
            >
              {item.type === 'video' ? (
                <video src={item.localPreview || item.url} className="w-full h-full object-cover" />
              ) : (
                <img src={item.localPreview || item.url} alt="" className="w-full h-full object-cover" />
              )}

              {/* Overlay Controls */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />

              {/* Drag Handle */}
              <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
                <div className="bg-black/60 rounded-lg p-1.5">
                  <GripVertical className="w-4 h-4 text-white" />
                </div>
              </div>

              {/* Delete Button */}
              <button
                onClick={(e) => { e.stopPropagation(); removeMedia(index); }}
                className="absolute top-2 right-2 w-8 h-8 bg-black/60 hover:bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all"
                data-testid={`media-delete-${index}`}
              >
                <X className="w-4 h-4" />
              </button>

              {/* Cover Badge */}
              {index === 0 && (
                <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/70 text-white text-xs px-2.5 py-1 rounded-full" data-testid="cover-badge">
                  <Star className="w-3 h-3 fill-current" /> Cover
                </div>
              )}

              {/* Index */}
              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Helper Text */}
      <div className="mt-4 space-y-1">
        <p className="text-sm text-slate-500">
          The first photo will be used as the cover image. Drag to reorder.
        </p>
        {!isMinMet && (
          <p className="text-sm text-red-500 flex items-center gap-1" data-testid="media-min-error">
            <AlertCircle className="w-3.5 h-3.5" />
            Add at least {MIN_REQUIRED_IMAGES - imageCount} more photo{MIN_REQUIRED_IMAGES - imageCount > 1 ? 's' : ''} to continue
          </p>
        )}
      </div>
    </div>
  );
}

// ===================== MAIN COMPONENT =====================
export default function AddProperty() {
  const navigate = useNavigate();
  const { isOwner } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(defaultFormData);
  const [loading, setLoading] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: hasValidApiKey ? GOOGLE_MAPS_API_KEY : '',
    libraries,
    preventGoogleFontsLoading: !hasValidApiKey,
  });

  const progress = (currentStep / steps.length) * 100;

  const updateFormData = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.property_type) { toast.error('Please select a property type'); return false; }
        break;
      case 2: {
        const errors = {};
        if (!formData.location.country.trim()) errors.country = 'Country is required';
        if (!formData.location.city.trim()) errors.city = 'City is required';
        const streetAddr = formData.location.street_address.trim();
        if (!streetAddr) {
          errors.street_address = 'Street address is required';
        } else if (/^-?\d+\.\d+\s*,\s*-?\d+\.\d+$/.test(streetAddr)) {
          errors.street_address = 'Please confirm a valid property address. Coordinates alone are not accepted.';
        }
        if (formData.location.latitude == null || formData.location.longitude == null) {
          errors.coords = 'Please enter the street address or pin the exact location on the map.';
        }
        if (Object.keys(errors).length > 0) {
          toast.error('Please complete all required location fields');
          window.__locationErrors = errors;
          return false;
        }
        break;
      }
      case 3:
        if (!formData.title.trim()) { toast.error('Please enter a property title'); return false; }
        if (!formData.monthly_rent) { toast.error('Please enter the rent amount'); return false; }
        break;
      case 6: {
        const imgCount = formData.media.filter(m => m.type === 'image').length;
        if (imgCount < MIN_REQUIRED_IMAGES) {
          toast.error(`Add at least ${MIN_REQUIRED_IMAGES} photos to continue`);
          return false;
        }
        break;
      }
      default:
        break;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) setCurrentStep(prev => Math.min(prev + 1, steps.length));
  };

  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        monthly_rent: parseFloat(formData.monthly_rent),
        deposit: parseFloat(formData.deposit) || 0,
        location: {
          ...formData.location,
          latitude: formData.location.latitude || 0,
          longitude: formData.location.longitude || 0,
        },
        details: {
          ...formData.details,
          floor_number: formData.details.floor_number ? parseInt(formData.details.floor_number) : null,
          total_floors: formData.details.total_floors ? parseInt(formData.details.total_floors) : null,
          area_sqft: formData.details.area_sqft ? parseFloat(formData.details.area_sqft) : null,
        },
        media: formData.media.map(m => ({
          url: m.url,
          name: m.name,
          type: m.type,
        })),
      };

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/properties`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Property created successfully!');
        navigate(`/property/${data.property_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create property');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to create property');
    } finally {
      setLoading(false);
    }
  };

  const canContinue = () => {
    if (currentStep === 6) {
      return formData.media.filter(m => m.type === 'image').length >= MIN_REQUIRED_IMAGES;
    }
    return true;
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h2 className="font-serif text-2xl text-slate-900 mb-2">Become an Owner First</h2>
              <p className="text-slate-500 mb-6">
                You need to be a property owner to list properties. Visit your dashboard to upgrade.
              </p>
              <Button onClick={() => navigate('/dashboard')} className="rounded-full">Go to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="add-property-page">
      <Navbar />
      <div className="pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Progress Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-serif text-2xl text-slate-900">Add New Property</h1>
              <span className="text-sm text-slate-500">Step {currentStep} of {steps.length}</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Step Indicators */}
          <div className="hidden md:flex items-center justify-between mb-8 overflow-x-auto pb-2">
            {steps.map((step, index) => (
              <div key={step.id} className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}>
                <button
                  onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                    step.id === currentStep ? 'bg-accent text-white'
                    : step.id < currentStep ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    : 'bg-slate-100 text-slate-400'
                  }`}
                  data-testid={`step-${step.id}`}
                >
                  <step.icon className="w-4 h-4" />
                  <span className="text-sm font-medium whitespace-nowrap">{step.title}</span>
                </button>
                {index < steps.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-2 ${step.id < currentStep ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          <Card className="border-0 shadow-soft">
            <CardContent className="p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Step 1: Property Type */}
                  {currentStep === 1 && (
                    <div>
                      <h2 className="font-serif text-2xl text-slate-900 mb-2">What type of property?</h2>
                      <p className="text-slate-500 mb-8">Select the type that best describes your property</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {propertyTypes.map((type) => (
                          <button
                            key={type.value}
                            onClick={() => updateFormData(null, 'property_type', type.value)}
                            className={`p-6 rounded-2xl border-2 transition-all ${
                              formData.property_type === type.value
                                ? 'border-accent bg-accent/5' : 'border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`property-type-${type.value}`}
                          >
                            <Building2 className={`w-8 h-8 mb-3 ${formData.property_type === type.value ? 'text-accent' : 'text-slate-400'}`} />
                            <span className={`font-medium ${formData.property_type === type.value ? 'text-accent' : 'text-slate-700'}`}>
                              {type.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Location */}
                  {currentStep === 2 && (
                    <LocationStep formData={formData} setFormData={setFormData} isLoaded={isLoaded} />
                  )}

                  {/* Step 3: Basic Details */}
                  {currentStep === 3 && (
                    <div>
                      <h2 className="font-serif text-2xl text-slate-900 mb-2">Property Details</h2>
                      <p className="text-slate-500 mb-8">Tell renters about your property</p>
                      <div className="space-y-6">
                        <div>
                          <Label>Property Title *</Label>
                          <Input value={formData.title} onChange={(e) => updateFormData(null, 'title', e.target.value)}
                            placeholder="Cozy 2BR Apartment in Downtown" className="mt-2" data-testid="property-title" />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Textarea value={formData.description} onChange={(e) => updateFormData(null, 'description', e.target.value)}
                            placeholder="Describe your property, its features, and what makes it special..." className="mt-2 min-h-32" data-testid="property-description" />
                        </div>
                        <div className="grid md:grid-cols-4 gap-4">
                          <div>
                            <Label>Rent Amount *</Label>
                            <Input type="number" value={formData.monthly_rent} onChange={(e) => updateFormData(null, 'monthly_rent', e.target.value)}
                              placeholder="1500" className="mt-2" data-testid="monthly-rent" />
                          </div>
                          <div>
                            <Label>Billing Period</Label>
                            <Select value={formData.billing_period} onValueChange={(v) => updateFormData(null, 'billing_period', v)}>
                              <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                              <SelectContent>{billingPeriods.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}</SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Deposit</Label>
                            <Input type="number" value={formData.deposit} onChange={(e) => updateFormData(null, 'deposit', e.target.value)}
                              placeholder="3000" className="mt-2" />
                          </div>
                          <div>
                            <Label>Currency</Label>
                            <Select value={formData.currency} onValueChange={(v) => updateFormData(null, 'currency', v)}>
                              <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                              <SelectContent className="max-h-60">{currencies.map((c) => (<SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>))}</SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                          <Label className="cursor-pointer">Price is negotiable</Label>
                          <Switch checked={formData.negotiable} onCheckedChange={(v) => updateFormData(null, 'negotiable', v)} />
                        </div>
                        <div className="grid md:grid-cols-4 gap-4">
                          <div>
                            <Label>Bedrooms</Label>
                            <Input type="number" min="0" value={formData.details.bedroom_count}
                              onChange={(e) => updateFormData('details', 'bedroom_count', parseInt(e.target.value) || 0)} className="mt-2" />
                          </div>
                          <div>
                            <Label>Bathrooms</Label>
                            <Input type="number" min="0" value={formData.details.bathroom_count}
                              onChange={(e) => updateFormData('details', 'bathroom_count', parseInt(e.target.value) || 0)} className="mt-2" />
                          </div>
                          <div>
                            <Label>Area (sq ft)</Label>
                            <Input type="number" value={formData.details.area_sqft}
                              onChange={(e) => updateFormData('details', 'area_sqft', e.target.value)} className="mt-2" />
                          </div>
                          <div>
                            <Label>Furnishing</Label>
                            <Select value={formData.details.furnishing} onValueChange={(v) => updateFormData('details', 'furnishing', v)}>
                              <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="furnished">Furnished</SelectItem>
                                <SelectItem value="semi_furnished">Semi Furnished</SelectItem>
                                <SelectItem value="unfurnished">Unfurnished</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Occupancy & Rules */}
                  {currentStep === 4 && (
                    <div>
                      <h2 className="font-serif text-2xl text-slate-900 mb-2">Occupancy & House Rules</h2>
                      <p className="text-slate-500 mb-8">Set preferences for ideal tenants</p>
                      <div className="space-y-6">
                        <div>
                          <Label>Maximum Renters</Label>
                          <Input type="number" min="1" value={formData.occupancy.max_renters}
                            onChange={(e) => updateFormData('occupancy', 'max_renters', parseInt(e.target.value) || 1)} className="mt-2 max-w-32" />
                        </div>
                        <div>
                          <Label className="mb-4 block">Allowed Tenants</Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {[{key:'male_allowed',label:'Males'},{key:'female_allowed',label:'Females'},{key:'family_allowed',label:'Families'},{key:'couples_allowed',label:'Couples'},{key:'students_allowed',label:'Students'},{key:'professionals_allowed',label:'Professionals'}].map((item) => (
                              <div key={item.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                <span className="text-slate-700">{item.label}</span>
                                <Switch checked={formData.occupancy[item.key]} onCheckedChange={(v) => updateFormData('occupancy', item.key, v)} />
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="mb-4 block">House Rules</Label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[{key:'smoking_allowed',label:'Smoking'},{key:'alcohol_allowed',label:'Alcohol'},{key:'pets_allowed',label:'Pets'},{key:'visitors_allowed',label:'Visitors'}].map((item) => (
                              <div key={item.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                <span className="text-slate-700">{item.label}</span>
                                <Switch checked={formData.rules[item.key]} onCheckedChange={(v) => updateFormData('rules', item.key, v)} />
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label>Custom Rules (optional)</Label>
                          <Textarea value={formData.rules.custom_rules} onChange={(e) => updateFormData('rules', 'custom_rules', e.target.value)}
                            placeholder="Any additional rules or requirements..." className="mt-2" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 5: Facilities */}
                  {currentStep === 5 && (
                    <div>
                      <h2 className="font-serif text-2xl text-slate-900 mb-2">Amenities & Facilities</h2>
                      <p className="text-slate-500 mb-8">Select all facilities available in your property</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {[{key:'wifi',label:'WiFi'},{key:'water',label:'Water Supply'},{key:'electricity_backup',label:'Power Backup'},{key:'attached_bathroom',label:'Attached Bathroom'},{key:'shared_bathroom',label:'Shared Bathroom'},{key:'kitchen_access',label:'Kitchen Access'},{key:'bike_parking',label:'Bike Parking'},{key:'car_parking',label:'Car Parking'},{key:'laundry',label:'Laundry'},{key:'balcony',label:'Balcony'},{key:'cctv',label:'CCTV'},{key:'security_guard',label:'Security Guard'},{key:'elevator',label:'Elevator'},{key:'air_conditioning',label:'AC'},{key:'heating',label:'Heating'},{key:'nearby_transport',label:'Near Transport'},{key:'nearby_market',label:'Near Market'},{key:'nearby_hospital',label:'Near Hospital'},{key:'nearby_school',label:'Near School'}].map((item) => (
                          <button key={item.key}
                            onClick={() => updateFormData('facilities', item.key, !formData.facilities[item.key])}
                            className={`p-4 rounded-xl border-2 transition-all text-left ${
                              formData.facilities[item.key] ? 'border-accent bg-accent/5' : 'border-slate-200 hover:border-slate-300'
                            }`}>
                            <div className="flex items-center gap-2">
                              {formData.facilities[item.key] && <Check className="w-4 h-4 text-accent" />}
                              <span className={formData.facilities[item.key] ? 'text-accent font-medium' : 'text-slate-700'}>{item.label}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 6: Media */}
                  {currentStep === 6 && (
                    <MediaStep formData={formData} setFormData={setFormData} />
                  )}

                  {/* Step 7: Review */}
                  {currentStep === 7 && (
                    <div>
                      <h2 className="font-serif text-2xl text-slate-900 mb-2">Review Your Listing</h2>
                      <p className="text-slate-500 mb-8">Make sure everything looks good before publishing</p>
                      <div className="space-y-6">
                        <div className="p-6 bg-slate-50 rounded-2xl">
                          <h3 className="font-medium text-slate-900 mb-4">Property Info</h3>
                          <div className="grid md:grid-cols-2 gap-4 text-sm">
                            <div><span className="text-slate-500">Type:</span> <span className="text-slate-900 capitalize">{formData.property_type}</span></div>
                            <div><span className="text-slate-500">Title:</span> <span className="text-slate-900">{formData.title}</span></div>
                            <div><span className="text-slate-500">Location:</span> <span className="text-slate-900">{formData.location.street_address}, {formData.location.city}, {formData.location.country}{formData.location.landmark ? ` (near ${formData.location.landmark})` : ''}</span></div>
                            <div><span className="text-slate-500">Rent:</span> <span className="text-slate-900">{formData.currency} {formData.monthly_rent}/{formData.billing_period}</span></div>
                            {formData.location.latitude && (
                              <div><span className="text-slate-500">Coordinates:</span> <span className="text-slate-900">{formData.location.latitude.toFixed(4)}, {formData.location.longitude.toFixed(4)}</span></div>
                            )}
                            <div><span className="text-slate-500">Photos:</span> <span className="text-slate-900">{formData.media.filter(m=>m.type==='image').length} images</span></div>
                          </div>
                        </div>
                        {formData.media.length > 0 && (
                          <div className="grid grid-cols-4 gap-2">
                            {formData.media.slice(0, 4).map((m, i) => (
                              <div key={i} className="relative aspect-square rounded-xl overflow-hidden">
                                <img src={m.localPreview || m.url} alt="" className="w-full h-full object-cover" />
                                {i === 0 && <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">Cover</div>}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="p-4 bg-amber-50 rounded-xl">
                          <p className="text-sm text-amber-800">Your property will be saved as inactive. You can publish it from your dashboard after review.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
                <Button variant="outline" onClick={prevStep} disabled={currentStep === 1} className="rounded-full" data-testid="prev-step-btn">
                  <ChevronLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                {currentStep < steps.length ? (
                  <Button onClick={nextStep}
                    disabled={currentStep === 6 && !canContinue()}
                    className={`rounded-full ${canContinue() ? 'bg-accent hover:bg-accent/90' : 'bg-slate-300'}`}
                    data-testid="next-step-btn">
                    Continue <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={loading} className="bg-accent hover:bg-accent/90 rounded-full" data-testid="submit-property-btn">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Check className="w-4 h-4 mr-2" /> Create Property</>}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
